import { Delete as DeleteIcon } from '@mui/icons-material';
import {
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { ItemErrorCodes } from '@nohara-dxdev/mt-common';
import { permission } from '@nohara-dxdev/oidc-core';
import { OidcProtectedComponent, useScopeIds } from '@nohara-dxdev/react-oidc';
import { isAxiosError } from 'axios';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/confirm-dialog';

import { useDeleteItemTemplate } from '../api/delete-item-template';

const deleteItemTemplatePermissions = [permission.itemTemplate.delete];

interface DeleteItemTemplateProps {
  itemTemplateId: string;
}

export const DeleteItemTemplate = ({ itemTemplateId }: DeleteItemTemplateProps) => {
  const [open, setOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const { t } = useTranslation();
  const deleteItemTemplate = useDeleteItemTemplate();
  const scopeIds = useScopeIds();
  const subject = useMemo(() => ({ ...scopeIds, itemTemplateId }), [scopeIds, itemTemplateId]);

  const handleClick = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleErrorDialogClose = () => {
    setErrorDialogOpen(false);
  };

  const handleConfirm = async () => {
    try {
      await deleteItemTemplate.mutateAsync(itemTemplateId);
      handleClose();
    } catch (error) {
      // Check if this is the "template is instantiated" error
      if (
        isAxiosError(error) &&
        error.response?.status === 400 &&
        error.response.data?.message === ItemErrorCodes.ITEM_TEMPLATE_IS_INSTANTIATED
      ) {
        handleClose();
        setErrorDialogOpen(true);
        return; // Don't re-throw this error
      }
      // For other errors, re-throw so the mutation's error handler can show the notification
      throw error;
    }
  };

  return (
    <>
      <OidcProtectedComponent requireAll={deleteItemTemplatePermissions} subject={subject}>
        <Tooltip title={t('itemTemplates.delete.tooltip')}>
          <IconButton
            color="error"
            size="small"
            onClick={handleClick}
            disabled={deleteItemTemplate.isPending}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </OidcProtectedComponent>
      <ConfirmDialog
        open={open}
        title={t('itemTemplates.delete.title')}
        message={t('itemTemplates.delete.message')}
        onClose={handleClose}
        onConfirm={handleConfirm}
        loading={deleteItemTemplate.isPending}
      />
      <Dialog open={errorDialogOpen} onClose={handleErrorDialogClose}>
        <DialogTitle>{t('itemTemplates.delete.instantiatedError.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText color="error">
            {t('itemTemplates.delete.instantiatedError.message')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleErrorDialogClose} color="primary" autoFocus>
            {t('common.ok')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
